<script>
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import { form, field, combined } from "svelte-forms";
	import {
		required,
		min,
		max,
		email,
		pattern,
	} from "svelte-forms/validators";
	import ErrorMessageHandler from "../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import Loader from "../../components/Loader/Loader.svelte";
	import PageHeader from "../../components/PageHeader/PageHeader.svelte";
	import client from "../../services/client";
	import { errorMessage, loading } from "../../store/store";
	import { navigate } from "svelte-routing";
	import { onMount } from "svelte";
	import { extractErrorMessage } from "src/utils/misc";
	import CustomSelect from "../../components/CustomSelect/CustomSelect.svelte";
	import CustomInput from "../../components/CustomInput/CustomInput.svelte";
	import { brand } from "src/brand";

	export let isEdit;

	const firstName = field("firstName", "", [required()]);
	const lastName = field("lastName", "", [required()]);
	const userEmail = field("userEmail", "", [required(), email()]);
	const role = field("role", "", [required()]);
	const password = field("password", "", [required(), min(8), max(25)]);
	const countryCode = field("countryCode", brand.defaultCountryCode, [
		required(),
	]);
	const mobileNumber = field("mobileNumber", "", [
		required(),
		pattern(brand.mobileValidation.pattern),
	]);
	const designation = field("designation", "", [required()]);
	const department = field("department", "", [required()]);

	let emailUsed;

	let load = false;
	loading.subscribe((value) => {
		load = value;
	});

	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});

	const myForm = form(
		firstName,
		lastName,
		role,
		userEmail,
		countryCode,
		mobileNumber,
		designation,
		department,
		password
	);

	let rolesData = [];
	const fetchAllRole = async () => {
		try {
			const res = await client(`/v1/roles`, {
				method: "GET",
				version: "User",
			});
			rolesData = res.data;
		} catch (error) {
			console.log(error);
		}
	};

	async function fetchUser() {
		try {
			const res = await client(`/v1/users/${isEdit}`, {
				method: "GET",
				version: "User",
			});
			let data = res.data;
			$firstName.value = data.firstName;
			$lastName.value = data.lastName;
			$role.value = data.roleId;
			$userEmail.value = data.email;
			$countryCode.value = data.countryCode || brand.defaultCountryCode;
			$mobileNumber.value = data.mobile;
			$designation.value = data.designation;
			$department.value = data.department;
		} catch (err) {
			extractErrorMessage(err);
			console.log(err);
		}
	}

	onMount(() => {
		fetchAllRole();
		if (isEdit) {
			fetchUser();
		}
	});

	async function handleSubmit() {
		try {
			let data = {
				firstName: $firstName.value,
				lastName: $lastName.value,
				roleId: $role.value,
				countryCode: $countryCode.value,
				mobile: $mobileNumber.value,
				designation: $designation.value,
				department: $department.value,
				email: $userEmail.value,
			};
			if (!isEdit) {
				data.password = $password.value;
			} else {
				data.id = isEdit;
			}
			const res = await client("/v1/users", {
				method: isEdit ? "PUT" : "POST",
				version: "User",
				data,
			});

			if (res.status === 200) {
				navigate("/account/fms-account-management");
			}
		} catch (err) {
			extractErrorMessage(err);
			console.log(err.response);
		}
	}

	$: isInvalid =
		!$firstName.value ||
		!$lastName.value ||
		!$role.value ||
		!$countryCode.value ||
		!$mobileNumber.value ||
		!$designation.value ||
		!$department.value ||
		!$userEmail.value;
</script>

<Row>
	<Col>
		<ErrorMessageHandler message={err} />
		<div>
			<PageHeader title={`${isEdit ? "Edit" : "Create New"} Account`} />
			<Col>
				<Row>
					<Col>
						<label for="role" class="d-block">
							<span> Role* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomSelect
							type="text"
							options={rolesData}
							bind:value={$role.value}
							error={$myForm.hasError("role.required")}
							errorText="Role is required"
							defaultOption="Select Role"
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="email" class="d-block">
							<span> Email* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="email"
							bind:value={$userEmail.value}
							error={$myForm.hasError("userEmail.required") ||
								emailUsed ||
								$myForm.hasError("userEmail.not_an_email")}
							errorText={$myForm.hasError("userEmail.required")
								? "Email is required"
								: emailUsed
									? "Email already used"
									: $myForm.hasError("userEmail.not_an_email")
										? "Must be a valid email"
										: ""}
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="password" class="d-block">
							<span>Temporary Password* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="password"
							bind:value={$password.value}
							error={$myForm.hasError("password.required") ||
								$myForm.hasError("password.min") ||
								$myForm.hasError("password.max")}
							errorText={$myForm.hasError("password.required")
								? "Password is required"
								: $myForm.hasError("password.min")
									? "Password Must be a minimum of 8 characters"
									: $myForm.hasError("password.max")
										? "Password Must be a maximum of 25 characters"
										: ""}
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="password" class="d-block">
							<br />
							<h4 class="font-weight-bold">Account Details</h4>
						</label>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="firstName" class="d-block">
							<span> First Name* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="text"
							bind:value={$firstName.value}
							on:input={firstName.validate}
							error={$myForm.hasError("firstName.required")}
							errorText="Firstname is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="lastName" class="d-block">
							<span> Last Name* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="text"
							bind:value={$lastName.value}
							on:input={lastName.validate}
							error={$myForm.hasError("lastName.required")}
							errorText="Lastname is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="countryCode" class="d-block">
							<span> Country Code* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="text"
							bind:value={$countryCode.value}
							on:input={countryCode.validate}
							error={$myForm.hasError("countryCode.required")}
							errorText="Country Code is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="mobileNumber" class="d-block">
							<span> Mobile Number* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="text"
							bind:value={$mobileNumber.value}
							on:input={mobileNumber.validate}
							error={$myForm.hasError("mobileNumber.required") ||
								$myForm.hasError("mobileNumber.pattern")}
							errorText={$myForm.hasError("mobileNumber.required")
								? "Mobile Number is required"
								: $myForm.hasError("mobileNumber.pattern")
									? brand.mobileValidation.message
									: ""}
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="designation" class="d-block">
							<span> Designation* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="text"
							bind:value={$designation.value}
							on:input={designation.validate}
							error={$myForm.hasError("designation.required")}
							errorText="Designation is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="department" class="d-block">
							<span> Department* </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomInput
							type="text"
							bind:value={$department.value}
							on:input={department.validate}
							error={$myForm.hasError("department.required")}
							errorText="Department is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col class="d-flex justify-content-end">
				<Row>
					<Col>
						<Button class="themeButton" on:click={myForm.reset}
							>RESET</Button
						>
						<Button
							disabled={isInvalid}
							class="themeButton primary"
							on:click|once={() => handleSubmit()}
							on:click={myForm.validate}
						>
							{#if load}
								<Loader />
							{:else}
								{isEdit ? "EDIT" : "CREATE"}
							{/if}
						</Button>
					</Col>
				</Row>
			</Col>
		</div>
	</Col>
</Row>
